import React, { useEffect, useRef } from 'react';
import '../../../../../styles/fonts.scss';
import {
	CommandType,
	ContentConverterEditor,
	PrivateTemplateEditor,
	waitForFontsApplied,
} from '@start-set/wakadoo-editor-core';
import {
	AnyComponentStructure,
	IAIGenerationHintState,
} from '@start-set/wakadoo-editor-core/Types';
import IEditorCommand from '@start-set/wakadoo-editor-core/mutations/commands/IEditorCommand';
import { useNavigate, useParams } from 'react-router-dom';
import { notificationError } from '../../../../../components/Notifications/callNotifcation';
import { fileAPI } from '../../../../../entities/file/api/api';
import { privateTemplateModuleAPI } from '../../../../../entities/module/private-template-module/api/api';
import { IGetModuleResponse } from '../../../../../entities/module/private-template-module/api/types';
import {
	moduleWindowActions,
	setCreateModuleWindowOpen, setCreateTemplateWindowStructure,
} from '../../../../../entities/module/private-template-module/model/slice';
import { sketchAPI } from '../../../../../entities/sketch/api/api';
import { templateGalleryModuleApi } from '../../../../../entities/template-gallery-module';
import { templateModuleActions } from '../../../../../entities/template-module';
import { templatePrivateAPI } from '../../../../../entities/template/private-template/api/api';
import { teamTemplateAPI } from '../../../../../entities/template/team/api/api';
import { IPrivateTemplateData } from '../../../../../entities/template/types';
import { workshopTemplateAPI } from '../../../../../entities/template/workshop/template/api/api';
import {
	closeAllModals,
	resetAllResponses,
} from '../../../../../features/ai/model/slice/aiSlice';
import {
	addSendErrorListener, addSendSuccessListener,
	addStartSendListener, getSocket,
	sendCommands, setAvailableGeneration, setRecoveryTimeHours, setTotalGeneration,
	socketSlice,
} from '../../../../../features/websocket/model/slice';
import store from '../../../../../redux/store/store';
import {
	onCloseAIActionsModal,
	onOpenAIActionsModal
} from '../../../../../redux/thunk-creators/moduleWindow';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { useUploadImage } from '../../hooks/useUploadImage';
import { useUploadXlsx } from '../../hooks/useUploadXlsx';
import css from '../../styles/editor.module.scss';
import { TEMPLATE_TYPES } from '../../types/types';
import { ServerWebSocketType } from '../../../../../features/websocket/model/types/serverWebSocketType';
import SocketStatus from '../../../Editor/replication/response/SocketStatus';

export interface IPrivateTemplateEditorProps {
	sketchID: string;
}

/**
 * Этот метод отвечает за редактирование приватного шаблона компонента.
 * Он используется для загрузки и изменения данных шаблона, а также их сохранения.
 */
const ComponentPrivateTemplateEditor = (props: IPrivateTemplateEditorProps) => {
	const { sketchID } = props;

	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const uploadImageFile = useUploadImage();
	const uploadXlsxFile = useUploadXlsx();

	const refEditorContainer = useRef<HTMLDivElement | null>(null);

	let editor: PrivateTemplateEditor | null = null;

	const onBackPage = () => {
		navigate('/collection/template/storage');
		dispatch(closeAllModals());
		dispatch(templatePrivateAPI.util?.invalidateTags(['templates']));
	};

	const onSendOffer = () => {
		navigate(`/private-template-present/${sketchID}`);
		dispatch(closeAllModals());
	};

	const openCreateModuleWindow = (
		fn: (showPreview: (bytes: Blob) => void) => void,
		structure: AnyComponentStructure,
	): void => {
		const showPreview = (previewBlob: Blob) => {
			const reader = new FileReader();
			reader.onloadend = () => {
				if (reader.result) {
					dispatch(moduleWindowActions.setCreateModuleWindowPreview(reader.result as string));
				}
			};
			reader.readAsDataURL(previewBlob);
		};

		dispatch(setCreateModuleWindowOpen({ editor, active: true }));
		dispatch(setCreateTemplateWindowStructure(structure));

		fn(showPreview);
	};

	const onOpenModulesWindow = () => {
		if (editor === null || editor === undefined) {
			throw new Error('Editor is null or undefined');
		}

		requestAnimationFrame(() => {
			dispatch(templateModuleActions.setIsTemplateModuleActive({ editor, active: true }));
		});
	};

	const onCloseModulesWindow = () => {
		requestAnimationFrame(() => {
			dispatch(templateModuleActions.setIsTemplateModuleActive({ active: false }));
		});
	};

	const onOpenCreateCustomTableWindow = () => {
		requestAnimationFrame(() => {
			dispatch(moduleWindowActions.setIsCreateTableModalOpen(true));
		});
	};

	const sendFilteredCommands = (commands: IEditorCommand[]): void => {
		const filteredCommands = commands.filter(
			command => command.type !== CommandType.LAYER_SEQUENCES,
		);

		dispatch(sendCommands({
			commands: filteredCommands,
			templateType: TEMPLATE_TYPES.PRIVATE,
		}));
	};

	const onDownloadPDF = async (editor: PrivateTemplateEditor | null, template: IPrivateTemplateData) => {
		const structure = editor?.getStructure();

		if (structure) {
			const contentConvertEditor = new ContentConverterEditor({
				isEnableMouseWheel: false,
				isCanvasHeightAdaptive: true,
				imageFileSourcePrefix: '/api/file/user',
			});
			await contentConvertEditor.setStructure({
				...template,
				root: structure.root,
			})
			await contentConvertEditor.savePdf(template.name);
		} else {
			notificationError('Ошибка', 'Не удалось найти структуру для скачивания PDF');
		}
	};

	/**
	 * Загружает данные модуля организации по идентификатору.
	 * При успешной загрузке обновляется состояние.
	 * @param {string} moduleId - ID модуля для загрузки.
	 */
	const loadOrganizationModuleData = async (moduleId: string) => {
		try {
			const response = await dispatch(privateTemplateModuleAPI.endpoints.getModule.initiate({ id: moduleId })).unwrap();
			return response;
		} catch (error) {
			notificationError('Ошибка', 'Не удалось загрузить структуру модуля');
			console.log('Ошибка: загрузки модуля: ', error);
			return null;
		}
	};

	/** Выполняет загрузку модуля галереи */
	const loadGalleryModuleData = async (moduleId: string) => {
		try {
			const response = await dispatch(
				templateGalleryModuleApi.endpoints.getTemplateGalleryModule.initiate({ moduleId }),
			).unwrap();
			return response;
		} catch (error) {
			notificationError('Ошибка', 'Не удалось загрузить структуру модуля');
			console.log('Ошибка: загрузки модуля: ', error);
			return null;
		}
	};

	/** Выполняет ижнект модуля */
	const injectModuleStructure = (moduleData: IGetModuleResponse | null, editor: PrivateTemplateEditor | null, closeModulesWindow: () => void) => {
		if (moduleData) {
			closeModulesWindow();
			editor?.injectModule(moduleData.rootComponent);
		} else {
			console.error('Структура модуля не найдена');
		}
	};

	/** Устанавливает состояние "загрузка" в редакторе */
	const setStateLoadingSaveTemplate = (editor: PrivateTemplateEditor | null) => {
		if (editor) {
			editor.setStateLoadingSaveTemplate();
		}
	};

	/** Устанавливает состояние "успех" в редакторе */
	const setStateSuccessSaveTemplate = (editor: PrivateTemplateEditor | null) => {
		if (editor) {
			editor.setStateSuccessSaveTemplate();
		}
	};

	/** Устанавливает состояние "ошибка" в редакторе */
	const setStateErrorSaveTemplate = (editor: PrivateTemplateEditor | null) => {
		if (editor) {
			editor.setStateErrorSaveTemplate();
		}
	};

	const savePreviewFile = (fileID: string) => {
		dispatch(sketchAPI.endpoints?.update.initiate({
			preview: fileID,
			sketchId: sketchID,
		}))
			.unwrap()
			.catch(error => {
				throw new Error(error);
			});
	};

	/** Запускает общий алгоритм сохранения preview скетча на сервер */
	const onUpdateTemplatePreview = (previewBlob: Blob) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			if (reader.result) {
				const previewDataUrl = reader.result as string;
				dispatch(moduleWindowActions.setCreateModuleWindowPreview(previewDataUrl));

				dispatch(fileAPI.endpoints?.savePreview.initiate({
					name: `preview-${sketchID}-${new Date().toISOString()}`,
					bytes: previewDataUrl,
				}))
					.unwrap()
					.then(response => {
						savePreviewFile(response.id);
					})
					.then(() => {
						dispatch(teamTemplateAPI.util?.invalidateTags(['team']));
						dispatch(templatePrivateAPI.util?.invalidateTags(['templates']));
						dispatch(workshopTemplateAPI.util?.invalidateTags(['templates']));
					});
			}
		};
		reader.readAsDataURL(previewBlob);
	};

	const availableGeneration = useAppSelector(socketSlice.selectors.getAvailableGeneration);
	const totalGeneration = useAppSelector(socketSlice.selectors.getTotalGeneration);
	const recoveryTimeHours = useAppSelector(socketSlice.selectors.getRecoveryTimeHours);

	const handleBalanceUpdate = (event: MessageEvent) => {
	    const data = JSON.parse(event.data);
		const body = JSON.parse(data.body);

		const {
			current_balance,
			max_balance,
			next_date_update,
		} = body;

		dispatch(setAvailableGeneration(current_balance));
		dispatch(setTotalGeneration(max_balance));
		dispatch(setRecoveryTimeHours(next_date_update));
	}

	useEffect(() => {
		const state = store.getState();
		const socket = getSocket(state);

		if (socket && socket.readyState === WebSocket.OPEN) {
			socket.onmessage = (event) => {
				const data = JSON.parse(event.data);
				if (data.type === ServerWebSocketType.aiGenerationsBalance && SocketStatus.OK) {
					handleBalanceUpdate(event);
				}
			};

			socket.send(JSON.stringify({
				type: ServerWebSocketType.aiGenerationsBalance,
				body: JSON.stringify({}),
			}));
		}
	}, []);

	const socket = useAppSelector(socketSlice.selectors.getSocket);

	useEffect(() => {
		if (!refEditorContainer.current) return;

		dispatch(templatePrivateAPI.endpoints?.get.initiate({ id: sketchID }))
			.unwrap()
			.then(async (res) => {
				const { template, rootComponent } = res;

				const getAIGeneration = (): IAIGenerationHintState => ({
					availableGeneration,
					totalGeneration,
					recoveryTimeHours,
				});

				await waitForFontsApplied();

				editor = new PrivateTemplateEditor({
					onBackPage,
					onSendOffer,
					uploadXlsxFile,
					uploadImageFile,
					onOpenModulesWindow,
					onCloseModulesWindow,
					onOpenAIActionsModal,
					onCloseAIActionsModal,
					enableDebugMenu: false,
					openCreateModuleWindow,
					onUpdateTemplatePreview,
					onOpenCreateCustomTableWindow,
					isEnableTopBarTariffLabel: true,
					getServerConnectionStatus: () => true,
					imageFileSourcePrefix: '/api/file/user',
					onGetGenerationHintStructure: getAIGeneration,
					sendCommands(commands: IEditorCommand[]): void {
						sendFilteredCommands(commands);
					},
					onDownloadPDF: () => onDownloadPDF(editor, template),
					editorContainer: refEditorContainer.current as HTMLDivElement,
					onGoToTariff: () => window.location.href = 'https://wakadoo.pro/tarif'
				});

				await editor.setStructure({
					...template,
					root: rootComponent,
				});

				if (socket && socket.readyState === WebSocket.OPEN) {
					socket.send(JSON.stringify({
						type: ServerWebSocketType.aiGenerationsBalance,
						body: JSON.stringify({}),
					}));
				}

				/** Индикатор состояния редактора */
				dispatch(addStartSendListener({ listener: () => { setStateLoadingSaveTemplate(editor); } }));
				dispatch(addSendErrorListener({listener: () => {setStateErrorSaveTemplate(editor)}}));
				dispatch(addSendSuccessListener({listener: () => {setStateSuccessSaveTemplate(editor)}}));

				/** Инжект модуля организации */
				dispatch(
					templateModuleActions.setOnEntityModuleClick((moduleId: string) => {
						loadOrganizationModuleData(moduleId)
							.then((moduleData) => {
								injectModuleStructure(moduleData, editor, onCloseModulesWindow);
							})
							.catch((error) => {
								console.error(error);
							});
					})
				);

				/** Инжект модуля галереи */
				dispatch(
					templateModuleActions.setOnGalleryModuleClick((moduleId: string) => {
						loadGalleryModuleData(moduleId)
							.then((moduleData) => {
								injectModuleStructure(moduleData, editor, onCloseModulesWindow);
							})
							.catch((error) => {
								console.error(error);
							});
					})
				);
			})
			.catch((e) => {
				notificationError('Загрузка структуры', 'Возникла непредвиденная ошибка загрузки структуры.');
				console.log(e);
			});

		return () => {
			dispatch(socketSlice.actions.disconnectPrivateTemplate(sketchID));
			dispatch(resetAllResponses());
			editor && editor.destruct();
		};
	}, []);

	return (
		<div ref={refEditorContainer} className={`${css.editor} ${css.privateTemplateEditor}`} />
	);
};

export const PrivateTemplateEditorWithRouter = () => {
	const { id } = useParams();
	if (id === undefined) {
		return <p>error get sketch id</p>;
	}

	return <ComponentPrivateTemplateEditor sketchID={id} />;
};
